import React, { useEffect, useCallback, useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'

import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'

import PartnershipBanner from 'components/Common/Vendors/Partnership/PartnershipBanner'
import { LUXURY_ESCAPES } from 'constants/brands'

import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import * as InteractionStudioService from 'api/interactionStudio'

import Leaderboard from 'components/Common/Banner/Leaderboard'
import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { fetchBanners, fetchListBanners } from 'actions/ContentActions'
import { getPlacement } from 'lib/url/campaignUtils'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'

import GlobalSearchHeroContainer from 'components/Search/GlobalSearch/GlobalSearchHeroContainer'
import { TOUR_V2_SEARCH_HERO_IMAGE_ID } from 'constants/tours'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import { TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES } from 'constants/tours'
import OfferList, { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import GeoContext from 'contexts/geoContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import ToursPageHighIntentTours from './ToursPageHighIntentTours'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { EnabledFilters } from 'components/Search/type'
import TourV2TrendingDestinationsCarousel from 'components/OfferPage/TourV2/TourV2TrendingDestinationsCarousel'
import { getTourV2TrendingDestinations } from 'components/OfferPage/TourV2/TourV2TrendingLocationsList'
import TourUSPContainer from 'components/OfferPage/TourV2/TourUSPContainer'
import * as Analytics from 'analytics/analytics'
import { tourCarouselHomepage, tourTrendingDestination } from 'analytics/snowplow/events'
import TourSearchHeroContainer from 'components/Search/TourV2/TourSearchHeroContainer'
import TourSearchContainerPane, {
  TourSearchContainerPaneContent,
} from 'components/Search/TourV2/TourSearchContainerPane'
import TourV2ExclusiveOfferCarousel from 'components/OfferList/OfferListTiles/TourV2ExclusiveOfferCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getListName, productClickSnowplow } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import uuidV4 from 'lib/string/uuidV4Utils'
import Group from 'components/utils/Group'
import TourPageCategoriesCarousel from 'components/Tours/TourPageCategoriesCarousel'
import Places from 'constants/places'
import TourPageSearchCarousel from 'components/Tours/TourPageSearchCarousel'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import TourPageOperatorsCarousel from 'components/Tours/TourPageOperatorsCarousel'

const OffersContainerOld = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
`

const OfferListWrapper = styled(LayoutContainer)`
  padding-bottom: ${rem(16)};
  padding-top: ${rem(48)};

  > * + * {
    margin-top: ${rem(32)};
  }
`

const NoOffersMessage = styled.div`
  padding: ${rem(24)} 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  padding-bottom: ${rem(16)};
  padding-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(48)};
  }
`

const LayoutContainerSearchBarMobile = styled(LayoutContainer)`
  position: absolute;
  bottom: 0;
  transform:translateY(50%);
`

interface MappedStateProps {
  banners: Array<App.Banner>
  listBanners: Array<App.ListBanner>
  urlFilters: App.OfferListFilters
  toursVerticalName: string,
  standaloneCruiseEnabled: boolean,
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

const toursDestinations = ['Egypt', 'New York', 'Africa', 'Europe']

const enabledFilters: EnabledFilters = {
  holidayTypes: true,
  amenities: true,
  inclusions: true,
  locations: true,
  customerRating: false,
  priceFilter: false,
  offerTypes: false,
}

const categoryCarousels = [
  {
    code: 'luxexclusive',
    title: <>Explore our <i>exclusive</i> tours</>,
    locations: [Places.Japan, Places.Spain, Places.Vietnam],
  },
  {
    code: 'foodandculinarytours',
    title: <><i>Foodie</i> favourites</>,
    locations: [],
  },
  {
    code: 'wildlifesafaris',
    title: <>Be an explorer on a <i>safari</i> adventure</>,
    locations: [Places.SouthAfrica],
  },
  {
    code: 'railjourneys',
    title: <>Scenic <i>rail</i> journeys</>,
    locations: undefined,
  },
]

function ToursPage(props: Props & MappedStateProps) {
  const {
    urlFilters,
    banners,
    listBanners,
    toursVerticalName,
    standaloneCruiseEnabled,
  } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode, currentCurrency, partnership } = useContext(GeoContext)
  const isTourV2SearchEnabled = useTourV2SearchEnabled()
  const showOperatorCarousel = useMemo(() => {
    const ttcOperatedCurrencies = ['AUD', 'NZD', 'GBP', 'USD', 'CAD', 'EUR']
    return ttcOperatedCurrencies.includes(currentCurrency)
  }, [currentCurrency])

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
    dispatch(fetchListBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
  }, [dispatch])

  const resetFilters = useCallback(() => {
    dispatch(replace({}))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      return {
        ...urlFilters,
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: urlFilters.sortBy ?? 'vertical',
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled])

  const offerListExtras: Array<OfferListV2Item> = useMemo(() => {
    const topBanner = getPlacement(banners, 'top')

    const extras: Array<OfferListV2Item> = [
      {
        element: <PartnershipBanner bannerId="types-page" />,
        show: !!(partnership && !TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES.has(partnership.code)),
        id: 'partnership',
        position: 'start',
      },
      {
        element: <Leaderboard banners={topBanner} />,
        show: !!topBanner.length,
        id: 'leaderboard',
        position: 1,
      },
      {
        id: 'list-p1',
        position: 2,
        element: <BannerCarousel banners={listBanners.slice(0, 3)} />,
        show: !!listBanners[0],
      },
      {
        id: 'tour-usp',
        position: 3,
        element: <TourUSPContainer />,
        show: true,
      },
      {
        id: 'list-p4',
        position: 4,
        element: <Leaderboard banners={[listBanners[3]]} />,
        show: !!listBanners[3],
      },
      {
        id: 'list-p8',
        position: 8,
        element: <Leaderboard banners={[listBanners[4]]} />,
        show: !!listBanners[4],
      },
      {
        id: 'list-p12',
        position: 12,
        element: <Leaderboard banners={[listBanners[5]]} />,
        show: !!listBanners[5],
      },
      {
        element: <NoOffersMessage>
          <Heading variant="heading3">No offers available</Heading>
          <BodyText variant="large">There are no offers matching your selected filters.</BodyText>
          <TextLink onClick={resetFilters}>Reset filters</TextLink>
        </NoOffersMessage>,
        id: 'no-offers-message',
        position: 2,
        show: 'hasNoOffers',
      }]
    return extras
  }, [banners, listBanners, partnership, resetFilters])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  const listId = useMemo(() => uuidV4(), [])
  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()
  const onTourCarrouselAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.impression) {
      const { category } = data
      Analytics.track('view-tour-carousel-homepage', tourCarouselHomepage(category))
    }
    if (action === OfferListEvents.productClick && 'tour' in data) {
      const { tour, index, category } = data
      const listName = getListName('OfferList', `tourspage - ${category}`, 'carousel')
      productClickSnowplow(currentCurrency, productClickObject(tour, index + 1), { listId, listName })
    }
  }, [currentCurrency, listId])

  const onTourTopExclusiveAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.impression) {
      Analytics.track('view-tour-carousel-homepage', tourCarouselHomepage('topexclusive'))
    }
    if (action === OfferListEvents.productClick && 'offer' in data) {
      const { offer, position } = data
      const listName = getListName('OfferList', 'tourspage - topexclusive', 'carousel')
      productClickSnowplow(currentCurrency, productClickObject(offer, position + 1), { listId, listName })
    }
  }, [currentCurrency, listId])

  const onTourTrendingDestinationsAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.clientAction) {
      const { subject, action } = data
      Analytics.track('click-tour-trending-destinations', tourTrendingDestination(subject, action))
    }
  }, [])

  const onOfferListAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.productClick) {
      const { offer, index } = data
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index,
        list: 'homepage',
      })
    }
  }, [])

  const enableNewTourHomePage = config.TOUR_V2_NEW_HOMEPAGE_ENABLED

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <div>
      <HeadData
        title={`${headTitle} - ${config.title} ${currentRegionCode}`}
        description={description}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector abandonedCart hotLead tripReengagement />
      </ErrorBoundary>

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        {isTourV2SearchEnabled && enableNewTourHomePage && <TourSearchHeroContainer
          heroImageId={TOUR_V2_SEARCH_HERO_IMAGE_ID}
          title={<><i>Tour</i> the world with us</>}
          subtitle="Choose tours from diverse durations, locations, and types to craft your ideal adventure"
          gravity="north"
        >
          <TourSearchContainerPane>
            <TourSearchContainerPaneContent>
              <TourV2SearchControls />
            </TourSearchContainerPaneContent>
          </TourSearchContainerPane>
          <LayoutContainerSearchBarMobile>
            <GlobalMobileSearchHeroBar placeholder="Search tours in" placeholderTextList={toursDestinations} />
          </LayoutContainerSearchBarMobile>
        </TourSearchHeroContainer>}
        {isTourV2SearchEnabled && !enableNewTourHomePage && <GlobalSearchHeroContainer
          heroImageId={TOUR_V2_SEARCH_HERO_IMAGE_ID}
          title={toursVerticalName}
          gravity="north"
        >
          <GlobalSearchContainerPane>
            <GlobalSearchContainerPaneContent>
              <TourV2SearchControls />
            </GlobalSearchContainerPaneContent>
          </GlobalSearchContainerPane>
          <GlobalMobileSearchHeroBar placeholder="Search tours in" placeholderTextList={toursDestinations} />
        </GlobalSearchHeroContainer>}
        {enableNewTourHomePage && <>
          <OfferListEventsProvider onListEvent={onTourTopExclusiveAnalyticEvent}>
            <TourV2ExclusiveOfferCarousel filters={filters} tileStyle="hero" />
          </OfferListEventsProvider>
          <OffersContainer>
            <Group direction="vertical" gap={32} tabletGap={40}>
              <ErrorBoundary fallback={null}>
                <ToursPageHighIntentTours />
              </ErrorBoundary>
              <Group direction="vertical" gap={56} tabletGap={80}>
                <OfferListEventsProvider onListEvent={onTourTrendingDestinationsAnalyticEvent}>
                  <TourV2TrendingDestinationsCarousel locations={getTourV2TrendingDestinations()} />
                </OfferListEventsProvider>
                <OfferListEventsProvider onListEvent={onTourCarrouselAnalyticEvent}>
                  {Array.from(categoryCarousels.values()).slice(0, 1).map(carousel =>
                    <TourPageSearchCarousel
                      filters={filters}
                      key={carousel.code}
                      title={carousel.title}
                      category={carousel.code}
                      places={carousel.locations}
                      sortType="vertical"
                    />,
                  )}
                  <TourPageCategoriesCarousel />
                  {config.BRAND === LUXURY_ESCAPES && showOperatorCarousel && <TourPageOperatorsCarousel />}
                  {Array.from(categoryCarousels.values()).slice(1).map(carousel =>
                    <TourPageSearchCarousel
                      key={carousel.code}
                      title={carousel.title}
                      category={carousel.code}
                      places={carousel.locations}
                      sortType="vertical"
                    />,
                  )}
                </OfferListEventsProvider>
                <LayoutContainer>
                  <TourUSPContainer />
                </LayoutContainer>
              </Group>
            </Group>
          </OffersContainer>
        </>}
        {!enableNewTourHomePage &&
          <OffersContainerOld>
            <OfferListWrapper>
              <OfferListEventsProvider onListEvent={onTourTrendingDestinationsAnalyticEvent}>
                <TourV2TrendingDestinationsCarousel fullWidthOverflow={false} locations={getTourV2TrendingDestinations()} />
              </OfferListEventsProvider>
              <div>
                <OfferListFrontPageFilters
                  heading={<>Explore today's <i>exclusive</i> offers</>}
                  types={filterTypes}
                  existingFilters={filters}
                  enabledFilters={enabledFilters}
                />
              </div>
              <OfferListEventsProvider onListEvent={onOfferListAnalyticEvent}>
                <OfferList
                  filters={filters}
                  interstitials={offerListExtras}
                />
              </OfferListEventsProvider>
            </OfferListWrapper>
          </OffersContainerOld>}
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    listBanners: state.content.listBanners?.toursAndCruises ?? [],
    banners: state.content.banners.toursAndCruises,
    urlFilters: getUrlOfferListFilters(state),
    toursVerticalName: tourV2VerticalNameSelector(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
